var m = require('mithril');
var prop = require('mithril/stream');
var Model = require('../../libs/Model');
var base = require('../../../../../models/bacnet/bacnetScConnection');
var helpers = require('../../rest/models/formHelpers');
var loaders = require('../../layout/components/loaders');
var presentation = require('../scDiscoveryPresentation');

var bacnetScConnection = Object.assign(Object.create(base), Model);
bacnetScConnection.fields = base.fields;
bacnetScConnection.extrawidth = true;

var TLS_MIN_OPTIONS = [
  { name: 'TLS 1.3', value: 'TLSv1.3' },
  { name: 'TLS 1.2', value: 'TLSv1.2' }
];
var TLS_MAX_OPTIONS = [
  { name: 'No separate maximum', value: '' },
  { name: 'TLS 1.3', value: 'TLSv1.3' },
  { name: 'TLS 1.2', value: 'TLSv1.2' }
];

var SERVER_CERTIFICATE_FIELDS = [
  'certPath', 'keyPath', 'caPath', 'pfxPath', 'passphraseEnv',
  'encryptedTlsFiles', 'tlsFilesFingerprint'
];

bacnetScConnection.initForm = function (record) {
  Model.initForm.call(bacnetScConnection, record);
  var summary = record && record.certificateFiles || {};
  bacnetScConnection.certificateFormat = prop(summary.format || 'pem');
  bacnetScConnection.initialCertificateFormat = summary.format || 'pem';
  bacnetScConnection.certificatePassphrase = prop('');
  bacnetScConnection.certificateUploads = {
    caFile: null,
    certificateFile: null,
    privateKeyFile: null,
    pfxFile: null
  };
};

function selectedUploadCount() {
  return Object.keys(bacnetScConnection.certificateUploads || {}).filter(function (key) {
    return Boolean(bacnetScConnection.certificateUploads[key]);
  }).length;
}

function certificateUploadError() {
  var summary = bacnetScConnection.record && bacnetScConnection.record.certificateFiles || {};
  var format = bacnetScConnection.certificateFormat();
  var uploads = bacnetScConnection.certificateUploads || {};
  var replacing = selectedUploadCount() > 0 || format !== bacnetScConnection.initialCertificateFormat;
  if (!replacing && summary.configured) return '';
  if (summary.credentialEncryptionAvailable === false) {
    return 'Certificate upload storage is unavailable. Ask the Nimrod administrator to configure credential encryption.';
  }
  if (!uploads.caFile) return 'Choose the hub CA certificate file.';
  if (format === 'pfx' && !uploads.pfxFile) return 'Choose the PFX or P12 file.';
  if (format === 'pem' && (!uploads.certificateFile || !uploads.privateKeyFile)) {
    return 'Choose both the client certificate and private key files.';
  }
  return '';
}

function formData() {
  var data = new FormData();
  Object.keys(bacnetScConnection.fields).forEach(function (key) {
    if (SERVER_CERTIFICATE_FIELDS.indexOf(key) !== -1) return;
    data.append(key, bacnetScConnection.form[key].toJSON());
  });
  data.append('certificateFormat', bacnetScConnection.certificateFormat());
  data.append('certificatePassphrase', bacnetScConnection.certificatePassphrase());
  Object.keys(bacnetScConnection.certificateUploads).forEach(function (key) {
    var file = bacnetScConnection.certificateUploads[key];
    if (file) data.append(key, file, file.name);
  });
  return data;
}

function saveError(error) {
  if (Array.isArray(error)) bacnetScConnection.errors = error;
  else if (error && typeof error.error === 'string') bacnetScConnection.errors = [error.error];
  else if (typeof error === 'string') bacnetScConnection.errors = [error];
  else bacnetScConnection.errors = ['Could not save the BACnet/SC hub.'];
  m.redraw();
  return Promise.reject(error);
}

bacnetScConnection.save = function () {
  var uploadError = certificateUploadError();
  if (uploadError) return saveError(uploadError);
  return bacnetScConnection.validate(bacnetScConnection.form)
    .then(function () {
      bacnetScConnection.errors = [];
      return loaders.requestWithLoader({
        loader: 'submit',
        url: bacnetScConnection.record && bacnetScConnection.record['@rid']
          ? bacnetScConnection.getUrl(bacnetScConnection.record)
          : '/api/v1/' + bacnetScConnection.url,
        method: bacnetScConnection.record && bacnetScConnection.record['@rid'] ? 'PUT' : 'POST',
        body: formData()
      });
    })
    .catch(saveError);
};

function fileInput(fieldName, id, label, accept, currentName) {
  var selected = bacnetScConnection.certificateUploads[fieldName];
  return m('div.mb3', [
    m('label.db.mb1', { for: id }, label),
    m('input.input-reset.w-100.pa2.ba.b--black-20', {
      id: id,
      type: 'file',
      accept: accept,
      onchange: function (event) {
        bacnetScConnection.certificateUploads[fieldName] =
          event.target.files && event.target.files[0] || null;
      }
    }),
    selected
      ? m('small.db.gray.mt1', 'Selected: ' + selected.name)
      : currentName ? m('small.db.gray.mt1', 'Stored: ' + currentName) : null
  ]);
}

function certificateSummary(summary) {
  if (summary.error) return m('div.mb3.pa2.br2.bg-washed-red.dark-red', summary.error);
  if (summary.source === 'upload') {
    return m('div.mb3.pa2.br2.bg-washed-green.dark-green', [
      m('strong', 'Certificate files stored'),
      m('small.db.mt1', summary.format === 'pfx'
        ? [summary.caFileName, summary.pfxFileName].filter(Boolean).join(' · ')
        : [summary.caFileName, summary.certificateFileName, summary.privateKeyFileName].filter(Boolean).join(' · '))
    ]);
  }
  if (summary.source === 'filesystem') {
    return m('div.mb3.pa2.br2.bg-washed-yellow.dark-red', [
      m('strong', 'Existing certificate file paths'),
      m('small.db.mt1', 'Choose a complete set of files below to replace this legacy configuration.')
    ]);
  }
  return null;
}

bacnetScConnection.modalTitle = function (titleText) {
  return m('span.flex.items-center', [
    m('span', titleText),
    m('a.white.f3.ml2.pointer.no-underline', {
      href: '/docs/configuration/bacnet#bacnetsc-hubs',
      target: '_blank',
      rel: 'noopener noreferrer',
      title: 'Open BACnet/SC configuration docs',
      onclick: function (ev) { if (ev) ev.stopPropagation(); }
    }, m('i.ion-ios-help-outline'))
  ]);
};

bacnetScConnection.view = function () {
  var record = bacnetScConnection.record || {};
  var summary = record.certificateFiles || {};
  var format = bacnetScConnection.certificateFormat();
  return m('form', { role: 'form', enctype: 'multipart/form-data' }, [
    bacnetScConnection.errorView(),
    helpers.modelSwitch(
      bacnetScConnection,
      'enabled',
      'Enabled',
      'Enabled hub profiles maintain Nimrod\'s BACnet/SC connection when an enabled BACnet Host uses them.',
      1,
      0
    ),
    record['@rid'] ? m('div.mb3', [
      m('span.moon-gray.mr2', 'Runtime status:'),
      m('span', presentation.hubStatusLabel(record)),
      record.activeHub ? m('span.moon-gray.ml3', record.activeHub) : null,
      record.lastDiagnostic ? m('small.db.moon-gray.mt1', record.lastDiagnostic) : null
    ]) : null,
    helpers.section('Node identity', [
      helpers.columns([
        helpers.text(bacnetScConnection, 'name', 'Name', 'Reusable name shown when BACnet Hosts select this hub.'),
        helpers.text(bacnetScConnection, 'localVmac', 'Local VMAC', 'Six-byte node VMAC as 12 hexadecimal characters. Leave blank on create to generate one.')
      ]),
      helpers.text(bacnetScConnection, 'deviceUuid', 'Device UUID', 'BACnet/SC node UUID. Leave blank on create to generate one.')
    ]),
    helpers.section('Hub endpoints', [
      helpers.text(bacnetScConnection, 'primaryHubUrl', 'Primary hub URL', 'Secure WebSocket URL, for example wss://hub.example.com:8443/bacnet-sc.'),
      helpers.text(bacnetScConnection, 'failoverHubUrl', 'Failover hub URL', 'Optional second hub used after connection failure.'),
      helpers.text(bacnetScConnection, 'servername', 'TLS server name', 'Optional certificate server name override. Usually blank.')
    ]),
    helpers.section('Certificates', [
      certificateSummary(summary),
      summary.credentialEncryptionAvailable === false
        ? m('div.mb3.pa2.br2.bg-washed-red.dark-red',
          'Certificate upload storage is unavailable until credential encryption is configured.')
        : null,
      m('p.mt0.gray', summary.configured
        ? 'Leave the file choices blank to keep the stored certificate files. To replace them, choose a complete set.'
        : 'Upload the hub CA certificate and Nimrod client identity. Nimrod stores these files securely for the node connection.'),
      fileInput(
        'caFile',
        'bacnet-sc-ca-file',
        'Hub CA certificate',
        '.pem,.crt,.cer,application/x-pem-file',
        summary.caFileName
      ),
      m('div.mb3', [
        m('label.db.mb1', { for: 'bacnet-sc-certificate-format' }, 'Client identity format'),
        m('select.form-control.w-100', {
          id: 'bacnet-sc-certificate-format',
          value: format,
          onchange: function (event) {
            var nextFormat = event.target.value;
            bacnetScConnection.certificateFormat(nextFormat);
            if (nextFormat === 'pfx') {
              bacnetScConnection.certificateUploads.certificateFile = null;
              bacnetScConnection.certificateUploads.privateKeyFile = null;
            } else {
              bacnetScConnection.certificateUploads.pfxFile = null;
            }
          }
        }, [
          m('option', { value: 'pem' }, 'Separate certificate and private key'),
          m('option', { value: 'pfx' }, 'PFX or P12')
        ])
      ]),
      format === 'pfx'
        ? fileInput(
          'pfxFile',
          'bacnet-sc-pfx-file',
          'PFX or P12 file',
          '.pfx,.p12,application/x-pkcs12',
          summary.pfxFileName
        )
        : helpers.columns([
          fileInput(
            'certificateFile',
            'bacnet-sc-certificate-file',
            'Client certificate',
            '.pem,.crt,.cer,application/x-pem-file',
            summary.certificateFileName
          ),
          fileInput(
            'privateKeyFile',
            'bacnet-sc-private-key-file',
            'Private key',
            '.pem,.key,application/x-pem-file',
            summary.privateKeyFileName
          )
        ]),
      m('div.mb3', [
        m('label.db.mb1', { for: 'bacnet-sc-certificate-passphrase' }, 'Private key or PFX passphrase'),
        m('input.form-control.w-100', {
          id: 'bacnet-sc-certificate-passphrase',
          type: 'password',
          autocomplete: 'new-password',
          value: bacnetScConnection.certificatePassphrase(),
          oninput: function (event) { bacnetScConnection.certificatePassphrase(event.target.value); }
        }),
        m('small.db.gray.mt1', summary.hasPassphrase && !selectedUploadCount()
          ? 'A passphrase is stored. It remains unchanged unless the certificate files are replaced.'
          : 'Enter the passphrase only when the uploaded private key or PFX file requires one.')
      ])
    ]),
    helpers.details('Advanced TLS and reconnect settings', [
      helpers.columns([
        helpers.select(bacnetScConnection, 'tlsMinVersion', 'Minimum TLS version', TLS_MIN_OPTIONS),
        helpers.select(bacnetScConnection, 'tlsMaxVersion', 'Maximum TLS version', TLS_MAX_OPTIONS)
      ]),
      helpers.columns([
        helpers.text(bacnetScConnection, 'connectionWaitTimeout', 'Connect-Accept timeout ms'),
        helpers.text(bacnetScConnection, 'webSocketWaitTimeout', 'WebSocket timeout ms')
      ]),
      helpers.columns([
        helpers.text(bacnetScConnection, 'heartbeatInterval', 'Heartbeat interval ms'),
        helpers.text(bacnetScConnection, 'reconnectMin', 'Reconnect minimum ms')
      ]),
      helpers.text(bacnetScConnection, 'reconnectMax', 'Reconnect maximum ms')
    ], false)
  ]);
};

module.exports = bacnetScConnection;
